import { Client, IMessage } from '@stomp/stompjs';

import storage, { EStorageKey } from '@aqx-core/common/storage';

import { formatTopicId, formatTopicPath } from '../utils';
import { ISocketTopic } from './types';

export enum ESymbolMessageType {
  LIVE = 'LIVE'
}

export interface ISymbolTopicMessageData {
  A: number; // ask
  B: number; // bid
  L: number; // low
  H: number; // high
  T: number; // time
  S: string; // symbol
  LP: number; // lastPrice
}

export interface ISymbolTopicMessage {
  M: ESymbolMessageType;
  D: ISymbolTopicMessageData;
}

export interface ISymbolTopicReceiver {
  subscriptionKey: string;
  payload: (payload: ISymbolTopicMessage | undefined, symbol?: string) => void;
}

interface ISubscribeParams {
  symbol: string;
  receiver: ISymbolTopicReceiver;
}

interface IProps {
  client: Client;
}

class SymbolTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<ISymbolTopicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;
    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      let isKeyExisting = false;
      this.receivers[subPath]?.forEach((r) => {
        isKeyExisting = r.subscriptionKey === receiver.subscriptionKey;
      });

      if (!isKeyExisting) {
        this.receivers[subPath]?.add(receiver);
      }
    } else {
      this.receivers[subPath] = new Set([receiver]);
      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message, symbol), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { symbol, receiver } = params;
    const { subPath, topicId } = this.mapTopicIds(symbol);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]?.size !== 0) {
      let lastItem = null;
      this.receivers[subPath]?.forEach((r) => {
        if (r.subscriptionKey === receiver.subscriptionKey) {
          lastItem = r;
        }
      });

      if (lastItem) {
        this.receivers[subPath]?.delete(lastItem);
      }
    }

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client?.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
      delete this.receivers[subPath];
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  public unsubscribeAll = () => {
    Object.keys(this.receivers).forEach((subPath) => {
      try {
        this.client?.unsubscribe(formatTopicId(subPath));
      } catch (err) {}
    });
    this.receivers = {};
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  private onReceive = (id: string, message: IMessage, symbol?: string) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as ISymbolTopicMessage;

    receivers.forEach((receiver) => receiver.payload(payload, symbol));
  };

  private mapTopicIds = (symbol: string) => {
    const marketSubscription = storage.getItem(EStorageKey.MARKET_SUBSCRIPTION);
    const subPath = `symbol/${marketSubscription}/${symbol}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default SymbolTopic;
